/**
 * CsvDropzone - Import-Komponente für CSV/Excel
 *
 * Features:
 * - Drag & Drop + Klick-Auswahl
 * - CSV/Excel Parsing (.xlsx lazy-loaded)
 * - Header-Erkennung
 * - Feld-Mapping UI
 * - Vorschau mit Beispielwerten
 *
 * Guardrails:
 * - read-excel-file wird erst nach File-Drop geladen (nicht im initial bundle)
 * - Nur client-side parsing
 * - Max 10MB Dateigröße
 * - Nur .xlsx-Dateien bei Excel
 */

import { useState, useCallback, useRef, useMemo } from 'react';
import {
  Upload,
  FileSpreadsheet,
  X,
  AlertCircle,
  CheckCircle,
  ChevronRight,
  ArrowRight,
  ChevronLeft,
} from 'lucide-react';

// Max Dateigröße: 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Feld-Definition für Mapping
export interface ImportField {
  key: string;
  label: string;
  required: boolean;
  description?: string;
}

// Mapping-Ergebnis
export interface FieldMapping {
  [internalField: string]: string | null;
}

// Import-Ergebnis für Preview
export interface ImportPreviewRow {
  rowNumber: number;
  data: Record<string, unknown>;
  errors: string[];
  status: 'valid' | 'error' | 'warning';
}

export interface ImportResult {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  noop?: number;
  failed?: number;
  errors: string[];
  changes?: Array<{
    externalEmployeeId?: string;
    externalDeliveryId?: string;
    action: 'create' | 'update' | 'noop';
    diffs?: Array<{ field: string; from: string | null; to: string | null }>;
  }>;
}

interface CsvDropzoneProps {
  fields: ImportField[];
  onImport: (
    data: Record<string, unknown>[],
    mapping: FieldMapping,
    options?: { dryRun?: boolean; applyUpdates?: boolean },
  ) => Promise<ImportResult>;
  onPreview?: (
    data: Record<string, unknown>[],
    mapping: FieldMapping,
  ) => Promise<ImportPreviewRow[]>;
  title?: string;
  description?: string;
  className?: string;
  /** Zeigt Name-Split Option an (nur für User-Import relevant) */
  showNameSplit?: boolean;
  /** Callback zum Schließen des Modals (im Ergebnis-Step) */
  onClose?: () => void;
}

type Step = 'upload' | 'mapping' | 'preview' | 'review' | 'result';

export function CsvDropzone({
  fields,
  onImport,
  onPreview,
  title = 'Daten importieren',
  description,
  className = '',
  showNameSplit = false,
  onClose,
}: CsvDropzoneProps) {
  // State
  const [step, setStep] = useState<Step>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // CSV Data
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvData, setCsvData] = useState<Record<string, unknown>[]>([]);

  // Mapping
  const [mapping, setMapping] = useState<FieldMapping>({});

  // Preview & Results
  const [previewRows, setPreviewRows] = useState<ImportPreviewRow[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [dryRunResult, setDryRunResult] = useState<ImportResult | null>(null);
  const [previewPage, setPreviewPage] = useState(0);
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [applyUpdates, setApplyUpdates] = useState(true);
  const [reviewPage, setReviewPage] = useState(0);

  // Name-Split Option
  const [nameSplitColumn, setNameSplitColumn] = useState<string | null>(null);
  const [nameSplitMode, setNameSplitMode] = useState<'lastFirst' | 'firstLast' | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const PAGE_SIZE = 10;

  // ─────────────────────────────────────────────────────────────
  // File Handling
  // ─────────────────────────────────────────────────────────────

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Header-Erkennung: Nur über eindeutige Keywords (gehärtet)
  // ─────────────────────────────────────────────────────────────

  const HEADER_KEYWORDS = [
    'name',
    'vorname',
    'nachname',
    'firstname',
    'lastname',
    'e-mail',
    'email',
    'mail',
    'id',
    'personal',
    'personalnummer',
    'mitarbeiter',
    'employee',
    'chip',
    'rfid',
    'nfc',
    'chipnummer',
    'abteilung',
    'department',
    'kostenstelle',
    'anzeigename',
    'displayname',
  ];

  const detectIfFirstRowIsHeader = (firstRow: unknown[]): boolean => {
    // Gehärtete Regel: Header NUR wenn mindestens 1 Zelle ein typisches
    // Header-Keyword enthält. Sonst default = "keine Header".
    // Dies verhindert, dass echte Datenzeilen als Header interpretiert werden.

    let keywordMatches = 0;

    for (const val of firstRow) {
      const str = String(val || '')
        .trim()
        .toLowerCase();
      if (!str) continue;

      // Prüfe ob Zelle ein Header-Keyword enthält
      for (const keyword of HEADER_KEYWORDS) {
        if (str.includes(keyword)) {
          keywordMatches++;
          break; // Pro Zelle nur einmal zählen
        }
      }
    }

    // Mindestens 1 Keyword-Match erforderlich für Header-Erkennung
    return keywordMatches >= 1;
  };

  // ─────────────────────────────────────────────────────────────
  // Heuristik: Auto-Match basierend auf Spalteninhalt
  // ─────────────────────────────────────────────────────────────

  const analyzeColumnForAutoMatch = (
    columnValues: unknown[],
    colIndex: number,
  ): { type: 'employeeId' | 'fullName' | 'chipNumber' | null; score: number } => {
    // Sample: erste 20 nicht-leere Werte
    const sample = columnValues
      .map((v) => String(v || '').trim())
      .filter((v) => v.length > 0)
      .slice(0, 20);

    if (sample.length < 3) return { type: null, score: 0 };

    // Pattern-Zähler
    let numericCount = 0;
    const numericLengths: number[] = [];
    let commaNameCount = 0;

    for (const val of sample) {
      // Rein numerisch (Personalnummer oder Chipnummer)
      if (/^\d+$/.test(val)) {
        numericCount++;
        numericLengths.push(val.length);
      }
      // "Nachname, Vorname" Pattern
      if (/^[A-Za-zäöüÄÖÜß\-\s]+,\s*[A-Za-zäöüÄÖÜß\-\s]+/.test(val)) {
        commaNameCount++;
      }
    }

    const numericRatio = numericCount / sample.length;
    const commaNameRatio = commaNameCount / sample.length;

    // A) Personalnummer: >=80% numerisch, Länge typisch 4-8+
    if (numericRatio >= 0.8) {
      const avgLen = numericLengths.reduce((a, b) => a + b, 0) / numericLengths.length;
      if (avgLen >= 4) {
        return { type: 'employeeId', score: numericRatio + (avgLen >= 5 ? 0.1 : 0) };
      }
      // Kürzere Zahlen → eher Chipnummer
      if (avgLen >= 3 && avgLen <= 6) {
        return { type: 'chipNumber', score: numericRatio * 0.8 };
      }
    }

    // B) Vollname "Nachname, Vorname": >=60% mit Komma-Pattern
    if (commaNameRatio >= 0.6) {
      return { type: 'fullName', score: commaNameRatio };
    }

    return { type: null, score: 0 };
  };

  // ─────────────────────────────────────────────────────────────
  // Robuste Delimiter-Detection für CSV
  // ─────────────────────────────────────────────────────────────

  const detectDelimiter = (text: string): string => {
    const candidates = [';', ',', '\t'];
    const sampleLines = text
      .split(/\r?\n/)
      .filter((line) => line.trim())
      .slice(0, 10);

    if (sampleLines.length === 0) return ';';

    let bestDelimiter = ';';
    let bestScore = 0;

    for (const delim of candidates) {
      // Zähle Spalten pro Zeile
      const columnCounts = sampleLines.map((line) => {
        // Einfaches Splitting (ohne Quote-Handling für schnelle Detection)
        return line.split(delim).length;
      });

      // Score = durchschnittliche Spaltenanzahl * Konsistenz
      const avgCols = columnCounts.reduce((a, b) => a + b, 0) / columnCounts.length;
      const minCols = Math.min(...columnCounts);
      const maxCols = Math.max(...columnCounts);

      // Konsistenz: Je weniger Varianz, desto besser
      const consistency = minCols > 1 ? minCols / maxCols : 0;
      const score = avgCols * consistency;

      // Bevorzuge Delimiter mit mehr als 1 Spalte und hoher Konsistenz
      if (avgCols > 1 && score > bestScore) {
        bestScore = score;
        bestDelimiter = delim;
      }
    }

    return bestDelimiter;
  };

  // ─────────────────────────────────────────────────────────────
  // Manuelles CSV-Parsing (robust für leere Spalten + trailing delimiters)
  // ─────────────────────────────────────────────────────────────

  const parseCSVManually = (text: string, delimiter: string): unknown[][] => {
    const lines = text.split(/\r?\n/);
    const result: unknown[][] = [];

    for (const line of lines) {
      if (!line) {
        // Leere Zeile → leeres Array (wird später als _isEmpty markiert)
        result.push([]);
        continue;
      }

      // Split mit Erhaltung leerer Felder (auch trailing)
      const cells = line.split(delimiter).map((cell) => cell.trim());
      result.push(cells);
    }

    // Entferne trailing leere Zeilen
    while (result.length > 0 && result[result.length - 1].length === 0) {
      result.pop();
    }

    return result;
  };

  const parseFile = useCallback(
    async (file: File) => {
      // Dateigröße prüfen
      if (file.size > MAX_FILE_SIZE) {
        setError(`Datei zu groß. Maximum: ${MAX_FILE_SIZE / 1024 / 1024}MB`);
        return;
      }

      setIsProcessing(true);
      setError(null);

      try {
        const buffer = await file.arrayBuffer();
        const lowerFileName = file.name.toLowerCase();
        const isCSV = lowerFileName.endsWith('.csv') || file.type === 'text/csv';
        const isXlsx = lowerFileName.endsWith('.xlsx');

        let rawData: unknown[][];

        if (isCSV) {
          // ─── CSV: Manuelles Parsing mit Delimiter-Detection ───
          // Decode mit cp1252 Fallback für deutsche Umlaute
          let text: string;
          try {
            // Versuche erst UTF-8
            text = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
          } catch {
            // Fallback: Windows-1252 (Latin1)
            text = new TextDecoder('windows-1252').decode(buffer);
          }

          const delimiter = detectDelimiter(text);
          console.log(
            '[CSV Import] Erkannter Delimiter:',
            JSON.stringify(delimiter),
            'für Datei:',
            file.name,
          );

          rawData = parseCSVManually(text, delimiter);
          console.log(
            '[CSV Import] Erkannte Spaltenanzahl:',
            rawData[0]?.length,
            'Erste Zeile:',
            rawData[0],
          );
        } else if (isXlsx) {
          const { default: readXlsxFile } = await import('read-excel-file/browser');
          rawData = (await readXlsxFile(file)) as unknown[][];
        } else {
          throw new Error('Nur CSV- und .xlsx-Dateien werden unterstützt');
        }

        if (rawData.length < 1) {
          throw new Error('Datei enthält keine Daten');
        }

        // ─── Header-Erkennung: Hat die CSV einen Header? ───
        const firstRow = rawData[0] as unknown[];
        const hasHeader = detectIfFirstRowIsHeader(firstRow);

        let headers: string[];
        let dataStartIndex: number;

        if (hasHeader) {
          // Erste Zeile ist Header
          headers = firstRow
            .map((h: unknown) => String(h || '').trim())
            .filter((h) => h && !h.startsWith('Unnamed:'));
          dataStartIndex = 1;
        } else {
          // Keine Header → generische Spaltennamen "Spalte A", "Spalte B", ...
          const colCount = firstRow.length;
          headers = [];
          for (let i = 0; i < colCount; i++) {
            const letter =
              i < 26
                ? String.fromCharCode(65 + i)
                : String.fromCharCode(65 + Math.floor(i / 26) - 1) +
                  String.fromCharCode(65 + (i % 26));
            headers.push(`Spalte ${letter}`);
          }
          dataStartIndex = 0; // Erste Zeile ist Daten
        }

        if (rawData.length <= dataStartIndex) {
          throw new Error('Datei enthält keine Daten');
        }

        setCsvHeaders(headers);

        // Daten als Objekte - ALLE Zeilen behalten (inkl. leere für korrekte Zählung)
        const allRows: Record<string, unknown>[] = [];
        for (let i = dataStartIndex; i < rawData.length; i++) {
          const rowArray = rawData[i] as unknown[];
          const rowObj: Record<string, unknown> = {};
          let hasAnyValue = false;

          for (let j = 0; j < headers.length; j++) {
            const header = headers[j];
            if (header) {
              const val = rowArray[j];
              rowObj[header] = val;
              if (val !== null && val !== undefined && String(val).trim() !== '') {
                hasAnyValue = true;
              }
            }
          }

          // Markiere leere Zeilen für Backend-Zählung
          if (!hasAnyValue) {
            rowObj._isEmpty = true;
          }

          allRows.push(rowObj);
        }
        setCsvData(allRows);

        // ─── Auto-Mapping: Header-basiert ODER Inhalt-basiert ───
        const autoMapping: FieldMapping = {};
        let detectedNameColumn: string | null = null;

        if (hasHeader) {
          // Header-basiertes Mapping (wie bisher)
          for (const field of fields) {
            // Exakter Match
            const exactMatch = headers.find((h) => h.toLowerCase() === field.key.toLowerCase());
            if (exactMatch) {
              autoMapping[field.key] = exactMatch;
              continue;
            }
            // Label Match
            const labelMatch = headers.find((h) => h.toLowerCase() === field.label.toLowerCase());
            if (labelMatch) {
              autoMapping[field.key] = labelMatch;
              continue;
            }
            // Partial Match
            const partialMatch = headers.find(
              (h) =>
                h.toLowerCase().includes(field.key.toLowerCase()) ||
                field.key.toLowerCase().includes(h.toLowerCase()),
            );
            if (partialMatch) {
              autoMapping[field.key] = partialMatch;
            }
          }
        } else {
          // ─── Inhalt-basiertes Auto-Match (für CSVs ohne Header) ───
          const columnAnalysis: Array<{ header: string; type: string | null; score: number }> = [];

          for (let colIdx = 0; colIdx < headers.length; colIdx++) {
            const header = headers[colIdx];
            // Sammle Werte dieser Spalte
            const colValues = allRows.map((row) => row[header]);
            const analysis = analyzeColumnForAutoMatch(colValues, colIdx);
            columnAnalysis.push({ header, type: analysis.type, score: analysis.score });
          }

          // Beste Kandidaten finden
          const employeeIdCandidates = columnAnalysis
            .filter((c) => c.type === 'employeeId')
            .sort((a, b) => b.score - a.score);

          const fullNameCandidates = columnAnalysis
            .filter((c) => c.type === 'fullName')
            .sort((a, b) => b.score - a.score);

          const chipCandidates = columnAnalysis
            .filter((c) => c.type === 'chipNumber')
            .sort((a, b) => b.score - a.score);

          // Personalnummer: Erste Spalte mit employeeId-Pattern (typisch Spalte A)
          if (employeeIdCandidates.length > 0) {
            autoMapping.externalEmployeeId = employeeIdCandidates[0].header;
          }

          // Vollname: Beste Spalte mit Name-Pattern → für Split
          if (fullNameCandidates.length > 0) {
            detectedNameColumn = fullNameCandidates[0].header;
            // displayName wird auf diese Spalte gemappt (Split wird später aktiviert)
            autoMapping.displayName = detectedNameColumn;
          }

          // Chipnummer: Zuerst explizite chipNumber-Kandidaten prüfen
          if (chipCandidates.length > 0) {
            const chipCol = chipCandidates[0].header;
            if (chipCol !== autoMapping.externalEmployeeId) {
              autoMapping.rfidPrintedCode = chipCol;
            }
          }

          // Chipnummer Fallback: Wenn mehrere employeeId-Spalten erkannt wurden,
          // nimm die letzte als Chipnummer (typisch: Spalte A = Personalnr, letzte = Chipnr)
          if (
            !autoMapping.rfidPrintedCode &&
            employeeIdCandidates.length >= 2 &&
            headers.length >= 3
          ) {
            const lastNumericCol = employeeIdCandidates[employeeIdCandidates.length - 1].header;
            if (lastNumericCol !== autoMapping.externalEmployeeId) {
              autoMapping.rfidPrintedCode = lastNumericCol;
            }
          }
        }

        setMapping(autoMapping);

        // ─── Auto-aktiviere Name-Split wenn "Nachname, Vorname" erkannt ───
        if (detectedNameColumn && showNameSplit) {
          setNameSplitColumn(detectedNameColumn);
          setNameSplitMode('lastFirst'); // "Nachname, Vorname" Format
        }

        setSelectedFile(file);
        setStep('mapping');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Fehler beim Lesen der Datei');
      } finally {
        setIsProcessing(false);
      }
    },
    [fields, showNameSplit],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        parseFile(files[0]);
      }
    },
    [parseFile],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        parseFile(files[0]);
      }
    },
    [parseFile],
  );

  const handleClick = () => {
    inputRef.current?.click();
  };

  const reset = () => {
    setStep('upload');
    setSelectedFile(null);
    setCsvHeaders([]);
    setCsvData([]);
    setMapping({});
    setPreviewRows([]);
    setImportResult(null);
    setDryRunResult(null);
    setError(null);
    setPreviewPage(0);
    setShowOnlyErrors(false);
    setApplyUpdates(true);
    setReviewPage(0);
    setNameSplitColumn(null);
    setNameSplitMode(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Name Parsing (clientseitig, respektiert gewähltes Format)
  // ─────────────────────────────────────────────────────────────

  const parseName = (
    displayName: string,
    mode: 'lastFirst' | 'firstLast' | null,
  ): { firstName: string; lastName: string } | null => {
    const name = displayName.trim();
    if (!name) return null;

    const lastNamePrefixes = ['von', 'van', 'de', 'zu', 'der', 'den', 'vom', 'zum', 'zur'];

    // Format: "Nachname, Vorname" (lastFirst mit Komma)
    if (mode === 'lastFirst' && name.includes(',')) {
      const [leftPart, rightPart] = name.split(',', 2);
      const lastName = leftPart.trim();
      const firstName = rightPart?.trim() || '';
      if (lastName && firstName) {
        return { firstName, lastName };
      }
      return null;
    }

    // Format: "Vorname, Nachname" (firstLast mit Komma)
    if (mode === 'firstLast' && name.includes(',')) {
      const [leftPart, rightPart] = name.split(',', 2);
      const firstName = leftPart.trim();
      const lastName = rightPart?.trim() || '';
      if (firstName && lastName) {
        return { firstName, lastName };
      }
      return null;
    }

    // Format ohne Komma: Leerzeichen-basiert
    const tokens = name.split(/\s+/);
    if (tokens.length < 2) return null;

    if (mode === 'lastFirst') {
      // "Nachname Vorname" → erster Token = Nachname, Rest = Vorname
      // Aber mit Präfix-Unterstützung
      let lastNameEndIndex = 0;
      for (let i = 0; i < tokens.length - 1; i++) {
        if (i === 0 || lastNamePrefixes.includes(tokens[i].toLowerCase())) {
          lastNameEndIndex = i;
        } else {
          break;
        }
      }
      const lastName = tokens.slice(0, lastNameEndIndex + 1).join(' ');
      const firstName = tokens.slice(lastNameEndIndex + 1).join(' ');
      if (firstName && lastName) {
        return { firstName, lastName };
      }
      return null;
    }

    // Default / firstLast: "Vorname Nachname"
    let lastNameStartIndex = tokens.length - 1;
    for (let i = tokens.length - 2; i >= 1; i--) {
      if (lastNamePrefixes.includes(tokens[i].toLowerCase())) {
        lastNameStartIndex = i;
      } else {
        break;
      }
    }

    const firstName = tokens.slice(0, lastNameStartIndex).join(' ');
    const lastName = tokens.slice(lastNameStartIndex).join(' ');

    if (firstName && lastName) {
      return { firstName, lastName };
    }
    return null;
  };

  // ─────────────────────────────────────────────────────────────
  // Live-Preview für Name-Split (explizite Dependencies für Re-Render)
  // ─────────────────────────────────────────────────────────────

  const nameSplitPreview = useMemo(() => {
    if (!nameSplitColumn || !nameSplitMode) return null;

    // Sample-Wert aus erster Datenzeile
    const sampleRow = csvData[0];
    if (!sampleRow) return null;

    const sampleValue = String(sampleRow[nameSplitColumn] || '').trim();
    if (!sampleValue) return { sampleValue: '', parsed: null };

    const parsed = parseName(sampleValue, nameSplitMode);
    return { sampleValue, parsed };
  }, [csvData, nameSplitColumn, nameSplitMode]);

  // Processed data mit Name-Split angewendet
  const processedData = useMemo((): Record<string, unknown>[] => {
    if (!nameSplitColumn || !nameSplitMode) return csvData;

    return csvData.map((row) => {
      const nameValue = String(row[nameSplitColumn] || '').trim();
      if (!nameValue) return { ...row, _splitError: 'Kein Name' } as Record<string, unknown>;

      const parsed = parseName(nameValue, nameSplitMode);
      if (!parsed)
        return { ...row, _splitError: 'Name konnte nicht aufgeteilt werden' } as Record<
          string,
          unknown
        >;

      return {
        ...row,
        _parsedFirstName: parsed.firstName,
        _parsedLastName: parsed.lastName,
        _splitSuccess: true,
      } as Record<string, unknown>;
    });
  }, [csvData, nameSplitColumn, nameSplitMode]);

  // ─────────────────────────────────────────────────────────────
  // Mapping
  // ─────────────────────────────────────────────────────────────

  const updateMapping = (fieldKey: string, csvColumn: string | null) => {
    setMapping((prev) => ({ ...prev, [fieldKey]: csvColumn }));
  };

  const getMappingValidation = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    for (const field of fields) {
      if (field.required && !mapping[field.key]) {
        errors.push(`Pflichtfeld "${field.label}" ist nicht zugeordnet`);
      }
    }
    return { valid: errors.length === 0, errors };
  };

  const getSampleValue = (csvColumn: string | null): string => {
    if (!csvColumn) return '–';
    const firstRow = csvData[0];
    if (!firstRow) return '–';
    const value = firstRow[csvColumn];
    if (value === undefined || value === null || value === '') return '(leer)';
    return String(value).substring(0, 50);
  };

  // Mehrere Samples gleichmäßig über die Datei verteilt (5 Samples)
  const getSampleValues = (csvColumn: string | null, count = 5): string[] => {
    if (!csvColumn || csvData.length === 0) return [];

    const samples: string[] = [];
    const len = csvData.length;

    // Indizes: gleichmäßig verteilt (0%, 25%, 50%, 75%, 100%)
    const indices =
      count <= len
        ? Array.from({ length: count }, (_, i) => Math.floor((i / (count - 1)) * (len - 1)))
        : Array.from({ length: len }, (_, i) => i);

    // Deduplizieren der Indizes
    const uniqueIndices = [...new Set(indices)];

    for (const idx of uniqueIndices) {
      const row = csvData[idx];
      if (!row) continue;
      const value = row[csvColumn];
      if (value === undefined || value === null || String(value).trim() === '') {
        samples.push('–');
      } else {
        samples.push(String(value).substring(0, 40));
      }
    }

    return samples;
  };

  // ─────────────────────────────────────────────────────────────
  // Preview & Import
  // ─────────────────────────────────────────────────────────────

  const handlePreview = async () => {
    setIsProcessing(true);
    setError(null);
    setPreviewPage(0);

    try {
      if (onPreview) {
        const preview = await onPreview(processedData, mapping);
        setPreviewRows(preview);
      } else {
        // Lokale Preview mit Name-Split Validierung
        const preview: ImportPreviewRow[] = processedData.map((row, idx) => {
          const errors: string[] = [];
          let status: 'valid' | 'error' | 'warning' = 'valid';

          // Check Name-Split Fehler
          if (row._splitError) {
            errors.push(String(row._splitError));
            status = 'error';
          }

          return {
            rowNumber: idx + 2,
            data: row,
            errors,
            status,
          };
        });
        setPreviewRows(preview);
      }
      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler bei der Vorschau');
    } finally {
      setIsProcessing(false);
    }
  };

  // Paging wird in renderPreviewStep berechnet (mit Filter-Support)

  // Mapping mit Name-Split anpassen
  const getFinalMapping = () => {
    const finalMapping = { ...mapping };
    if (nameSplitColumn) {
      finalMapping.firstName = '_parsedFirstName';
      finalMapping.lastName = '_parsedLastName';
      // WICHTIG: displayName NICHT aus CSV-Rohwert übernehmen wenn Name-Split aktiv,
      // damit das API displayName aus firstName/lastName generiert ("Vorname Nachname")
      // statt das CSV-Format zu übernehmen ("Nachname, Vorname")
      delete finalMapping.displayName;
    }
    return finalMapping;
  };

  // DryRun für Review-Step
  const handleDryRun = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      const result = await onImport(processedData, getFinalMapping(), { dryRun: true });
      setDryRunResult(result);

      // Wenn Updates vorhanden -> Review-Step, sonst direkt importieren
      const updateChanges =
        result.changes?.filter((c) => c.action === 'update' && c.diffs && c.diffs.length > 0) || [];
      if (updateChanges.length > 0) {
        setReviewPage(0);
        setStep('review');
      } else {
        // Keine Updates -> direkt importieren
        await handleFinalImport();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Vorschau fehlgeschlagen');
    } finally {
      setIsProcessing(false);
    }
  };

  // Finaler Import
  const handleFinalImport = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      const result = await onImport(processedData, getFinalMapping(), {
        dryRun: false,
        applyUpdates,
      });
      setImportResult(result);
      setStep('result');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import fehlgeschlagen');
    } finally {
      setIsProcessing(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Render: Stepper (visuelle Orientierung)
  // ─────────────────────────────────────────────────────────────

  const STEPS = [
    { key: 'upload', label: '1. Datei' },
    { key: 'mapping', label: '2. Zuordnung' },
    { key: 'preview', label: '3. Vorschau' },
    { key: 'result', label: '4. Ergebnis' },
  ];

  const getStepIndex = () => {
    if (step === 'upload') return 0;
    if (step === 'mapping') return 1;
    if (step === 'preview' || step === 'review') return 2;
    if (step === 'result') return 3;
    return 0;
  };

  const renderStepper = () => (
    <div className="flex items-center justify-center gap-1 mb-4">
      {STEPS.map((s, i) => {
        const currentIdx = getStepIndex();
        const isActive = i === currentIdx;
        const isDone = i < currentIdx;
        return (
          <div key={s.key} className="flex items-center">
            <span
              className={`
              px-3 py-1 text-sm rounded-full transition-colors
              ${isActive ? 'bg-akzent text-akzent-satz font-medium' : ''}
              ${isDone ? 'bg-flaeche-2 text-lauf' : ''}
              ${!isActive && !isDone ? 'bg-flaeche-2 text-tinte-2' : ''}
            `}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 && (
              <div className={`w-8 h-0.5 mx-1 ${isDone ? 'bg-lauf' : 'bg-flaeche-2'}`} />
            )}
          </div>
        );
      })}
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  // Render: Upload Step
  // ─────────────────────────────────────────────────────────────

  const renderUploadStep = () => (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
        transition-colors duration-200
        ${
          isDragging
            ? 'border-akzent bg-akzent-tief'
            : 'border-linie-2 hover:border-linie-2 hover:bg-flaeche-2'
        }
        ${isProcessing ? 'opacity-50 pointer-events-none' : ''}
      `}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleClick();
      }}
      aria-label="Datei zum Importieren auswählen"
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx"
        onChange={handleFileInput}
        className="hidden"
        aria-hidden="true"
      />

      <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'text-akzent' : 'text-tinte-3'}`} />
      <p className="font-medium text-tinte-2">Datei hier ablegen</p>
      <p className="text-sm text-tinte-2 mt-1">oder klicken zum Auswählen</p>
      <p className="text-xs text-tinte-3 mt-2">CSV oder Excel (.xlsx)</p>

      {isProcessing && (
        <div className="absolute inset-0 flex items-center justify-center bg-flaeche/80 rounded-lg">
          <div className="animate-spin w-6 h-6 border-2 border-akzent border-t-transparent rounded-full" />
        </div>
      )}
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  // Render: Mapping Step
  // ─────────────────────────────────────────────────────────────

  const renderMappingStep = () => {
    const validation = getMappingValidation();

    return (
      <div className="space-y-4">
        {/* Datei-Info */}
        <div className="flex items-center gap-3 p-3 bg-flaeche-2 rounded-lg">
          <FileSpreadsheet className="w-6 h-6 text-lauf" />
          <div className="flex-1">
            <p className="font-medium">{selectedFile?.name}</p>
            <p className="text-sm text-tinte-2">
              {csvData.length} Zeilen, {csvHeaders.length} Spalten
            </p>
          </div>
          <button
            onClick={reset}
            className="p-1.5 text-tinte-3 hover:text-tinte-2 hover:bg-flaeche-2 rounded"
            title="Andere Datei wählen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Mapping Table - Dateizentriert (Spalten in Dateireihenfolge) */}
        <div className="border rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-flaeche-2 border-b">
            <h4 className="font-medium">Feld-Zuordnung</h4>
            <p className="text-xs text-tinte-2 mt-1">
              Ordnen Sie jede Datei-Spalte einem System-Feld zu
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-flaeche-2 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-tinte-2 uppercase">
                    Datei-Spalte
                  </th>
                  <th className="px-4 py-2 text-center w-10"></th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-tinte-2 uppercase w-48">
                    System-Feld
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-tinte-2 uppercase w-48">
                    Vorschau
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {csvHeaders.map((header, colIndex) => {
                  // Spaltenindex als Buchstabe (A, B, C, ... Z, AA, AB, ...)
                  const colLetter =
                    colIndex < 26
                      ? String.fromCharCode(65 + colIndex)
                      : String.fromCharCode(65 + Math.floor(colIndex / 26) - 1) +
                        String.fromCharCode(65 + (colIndex % 26));

                  // Finde welches System-Feld dieser Spalte zugeordnet ist
                  const assignedFieldKey =
                    Object.entries(mapping).find(([, csvCol]) => csvCol === header)?.[0] || null;
                  const assignedField = assignedFieldKey
                    ? fields.find((f) => f.key === assignedFieldKey)
                    : null;

                  // Sample-Werte für diese Spalte
                  const sampleVals = getSampleValues(header, 5);

                  return (
                    <tr key={header} className="hover:bg-flaeche-2">
                      {/* 1. Datei-Spalte (Quelle) */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-flaeche-2 text-tinte-2 text-xs font-mono rounded">
                            {colLetter}
                          </span>
                          <span className="font-medium text-tinte-2">{header}</span>
                        </div>
                      </td>
                      {/* 2. Pfeil */}
                      <td className="px-4 py-3 text-center">
                        <ArrowRight className="w-4 h-4 text-tinte-3 inline" />
                      </td>
                      {/* 3. System-Feld (Ziel) - Dropdown */}
                      <td className="px-4 py-3">
                        <select
                          value={assignedFieldKey || '_none'}
                          onChange={(e) => {
                            const newFieldKey = e.target.value === '_none' ? null : e.target.value;
                            // Alte Zuordnung entfernen
                            if (assignedFieldKey) {
                              updateMapping(assignedFieldKey, null);
                            }
                            // Neue Zuordnung setzen
                            if (newFieldKey) {
                              updateMapping(newFieldKey, header);
                            }
                          }}
                          className="w-full px-3 py-2 border border-linie-2 rounded-lg focus:ring-2 focus:ring-akzent focus:border-akzent bg-flaeche text-sm"
                        >
                          <option value="_none">– Ignorieren –</option>
                          {fields.map((field) => {
                            const isAlreadyMapped = !!(
                              mapping[field.key] && mapping[field.key] !== header
                            );
                            return (
                              <option key={field.key} value={field.key} disabled={isAlreadyMapped}>
                                {field.label}
                                {field.required ? ' *' : ''}
                                {isAlreadyMapped ? ` (→ ${mapping[field.key]})` : ''}
                              </option>
                            );
                          })}
                        </select>
                        {assignedField?.description && (
                          <p className="text-xs text-tinte-2 mt-1">{assignedField.description}</p>
                        )}
                      </td>
                      {/* 4. Vorschau */}
                      <td className="px-4 py-3 text-sm text-tinte-2">
                        {sampleVals.length > 0 ? (
                          <div className="space-y-0.5">
                            {sampleVals.map((v, i) => (
                              <div key={i} className={`text-xs ${v === '–' ? 'text-tinte-3' : ''}`}>
                                {v}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-tinte-3">–</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Name-Split Option für Vorname/Nachname (nur für User-Import) */}
        {showNameSplit && (
          <div className="border rounded-lg p-4 bg-akzent-tief border-linie-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!nameSplitColumn}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Default: erste Spalte die wie Name aussieht
                    const defaultCol =
                      csvHeaders.find((h) => {
                        const sample = getSampleValue(h);
                        return (
                          sample !== '–' &&
                          sample !== '(leer)' &&
                          (sample.includes(',') || sample.split(' ').length >= 2)
                        );
                      }) || csvHeaders[0];
                    setNameSplitColumn(defaultCol || null);
                    setNameSplitMode('lastFirst');
                    // Clear separate mappings
                    updateMapping('firstName', null);
                    updateMapping('lastName', null);
                  } else {
                    setNameSplitColumn(null);
                    setNameSplitMode(null);
                  }
                }}
                className="w-5 h-5 mt-0.5 rounded border-akzent text-akzent focus:ring-akzent"
              />
              <div className="flex-1">
                <p className="font-medium text-tinte">
                  Vorname & Nachname aus derselben Spalte ableiten
                </p>
                <p className="text-sm text-akzent">
                  Falls die Datei nur eine Spalte mit dem vollständigen Namen enthält
                </p>
              </div>
            </label>

            {nameSplitColumn && (
              <div className="mt-3 ml-8 space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-tinte-2">Quelle:</span>
                  <select
                    value={nameSplitColumn}
                    onChange={(e) => setNameSplitColumn(e.target.value)}
                    className="px-3 py-1.5 border border-akzent rounded-lg bg-flaeche text-sm"
                  >
                    {csvHeaders.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-tinte-2">Format:</span>
                  <select
                    value={nameSplitMode || 'lastFirst'}
                    onChange={(e) => setNameSplitMode(e.target.value as 'lastFirst' | 'firstLast')}
                    className="px-3 py-1.5 border border-akzent rounded-lg bg-flaeche text-sm"
                  >
                    <option value="lastFirst">Nachname, Vorname</option>
                    <option value="firstLast">Vorname Nachname</option>
                  </select>
                </div>

                {/* Live-Beispiel (verwendet useMemo für korrektes Re-Render bei Format-Wechsel) */}
                <div className="p-3 bg-flaeche rounded-lg border border-linie-2">
                  <p className="text-xs text-tinte-2 mb-1">Beispiel:</p>
                  <p className="text-sm">
                    CSV: <strong>"{nameSplitPreview?.sampleValue || ''}"</strong>
                  </p>
                  {nameSplitPreview?.parsed ? (
                    <p className="text-sm text-lauf mt-1">
                      → Vorname: <strong>{nameSplitPreview.parsed.firstName}</strong> / Nachname:{' '}
                      <strong>{nameSplitPreview.parsed.lastName}</strong>
                    </p>
                  ) : (
                    <p className="text-sm text-stoer mt-1">
                      → Aufteilung nicht möglich (Format nicht erkannt)
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Validation Errors */}
        {!validation.valid && (
          <div className="flex items-start gap-2 p-3 bg-flaeche-2 text-stoer rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              {validation.errors.map((err, i) => (
                <p key={i}>{err}</p>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between">
          <button
            onClick={reset}
            className="px-4 py-2 border border-linie-2 rounded-lg hover:bg-flaeche-2"
          >
            Abbrechen
          </button>
          <button
            onClick={handlePreview}
            disabled={!validation.valid || isProcessing}
            className="px-4 py-2 bg-akzent text-akzent-satz rounded-lg hover:bg-akzent disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            Vorschau
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────
  // Render: Preview Step
  // ─────────────────────────────────────────────────────────────

  const renderPreviewStep = () => {
    const validCount = previewRows.filter((r) => r.status === 'valid').length;
    const errorCount = previewRows.filter((r) => r.status === 'error').length;
    const errorRows = previewRows.filter((r) => r.status === 'error');

    // Zähle Zeilen ohne Chipnummer (nur wenn rfidPrintedCode gemappt ist)
    const rfidMapped = !!mapping.rfidPrintedCode;
    const rfidCol = mapping.rfidPrintedCode;
    const withoutChipCount = rfidMapped
      ? csvData.filter((row) => {
          const val = rfidCol ? String(row[rfidCol] || '').trim() : '';
          return !val;
        }).length
      : 0;
    const errorIndices = errorRows.map((r) => previewRows.indexOf(r));

    // Gefilterte Rows basierend auf showOnlyErrors
    const displayRows = showOnlyErrors ? errorRows : previewRows;
    const totalPages = Math.ceil(displayRows.length / PAGE_SIZE);
    const currentPageRows = displayRows.slice(
      previewPage * PAGE_SIZE,
      (previewPage + 1) * PAGE_SIZE,
    );
    const startRow = previewPage * PAGE_SIZE + 1;
    const endRow = Math.min((previewPage + 1) * PAGE_SIZE, displayRows.length);

    // Navigation zu Fehlern
    const goToNextError = () => {
      if (errorRows.length === 0) return;
      const currentIdx = previewPage * PAGE_SIZE;
      const nextError = errorIndices.find((i) => i > currentIdx);
      if (nextError !== undefined) {
        setPreviewPage(Math.floor(nextError / PAGE_SIZE));
      } else {
        // Wrap around
        setPreviewPage(Math.floor(errorIndices[0] / PAGE_SIZE));
      }
    };

    const goToPrevError = () => {
      if (errorRows.length === 0) return;
      const currentIdx = previewPage * PAGE_SIZE;
      const prevErrors = errorIndices.filter((i) => i < currentIdx);
      if (prevErrors.length > 0) {
        setPreviewPage(Math.floor(prevErrors[prevErrors.length - 1] / PAGE_SIZE));
      } else {
        // Wrap around
        setPreviewPage(Math.floor(errorIndices[errorIndices.length - 1] / PAGE_SIZE));
      }
    };

    return (
      <div className="space-y-4">
        {/* Summary */}
        <div className="flex items-center justify-between p-3 bg-flaeche-2 rounded-lg">
          <div>
            <p className="font-medium">{csvData.length} Datensätze</p>
            <p className="text-sm text-tinte-2">
              {validCount > 0 && <span className="text-lauf">{validCount} gültig</span>}
              {validCount > 0 && errorCount > 0 && ' · '}
              {errorCount > 0 && (
                <button
                  onClick={() => {
                    setShowOnlyErrors(true);
                    setPreviewPage(0);
                  }}
                  className="text-stoer hover:underline cursor-pointer"
                >
                  {errorCount} fehlerhaft
                </button>
              )}
              {rfidMapped && withoutChipCount > 0 && (
                <>
                  {(validCount > 0 || errorCount > 0) && ' · '}
                  <span className="text-tinte-3">{withoutChipCount} ohne Chipnummer</span>
                </>
              )}
            </p>
          </div>
          {/* Ansichts-Toggle: Alle / Nur Fehler */}
          {errorCount > 0 && (
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-lg border border-linie-2 p-0.5 bg-flaeche-2">
                <button
                  onClick={() => {
                    setShowOnlyErrors(false);
                    setPreviewPage(0);
                  }}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    !showOnlyErrors
                      ? 'bg-flaeche text-tinte shadow-sm font-medium'
                      : 'text-tinte-2 hover:text-tinte'
                  }`}
                >
                  Alle
                </button>
                <button
                  onClick={() => {
                    setShowOnlyErrors(true);
                    setPreviewPage(0);
                  }}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    showOnlyErrors
                      ? 'bg-stoer text-akzent-satz shadow-sm font-medium'
                      : 'text-stoer hover:bg-flaeche-2'
                  }`}
                >
                  Nur Fehler ({errorCount})
                </button>
              </div>
              {!showOnlyErrors && (
                <div className="flex items-center gap-1 ml-2">
                  <button
                    onClick={goToPrevError}
                    className="p-1 text-stoer hover:bg-flaeche-2 rounded"
                    title="Vorheriger Fehler"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-stoer">Fehler</span>
                  <button
                    onClick={goToNextError}
                    className="p-1 text-stoer hover:bg-flaeche-2 rounded"
                    title="Nächster Fehler"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Name-Split Info */}
          {nameSplitColumn && (
            <div className="text-sm text-akzent bg-akzent-tief px-3 py-1 rounded border border-linie-2">
              Name-Split aktiv: {nameSplitColumn}
            </div>
          )}
        </div>

        {/* Preview Table */}
        <div className="border rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-flaeche-2 border-b flex items-center justify-between">
            <h4 className="font-medium">
              {showOnlyErrors ? 'Fehler: ' : ''}Zeilen {startRow}–{endRow} von {displayRows.length}
              {showOnlyErrors && <span className="text-tinte-3 font-normal ml-1">(gefiltert)</span>}
            </h4>
            {/* Paging Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPreviewPage((p) => Math.max(0, p - 1))}
                disabled={previewPage === 0}
                className="p-1.5 border border-linie-2 rounded hover:bg-flaeche-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Vorherige Seite"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-tinte-2 min-w-[60px] text-center">
                {previewPage + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPreviewPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={previewPage >= totalPages - 1}
                className="p-1.5 border border-linie-2 rounded hover:bg-flaeche-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Nächste Seite"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-flaeche-2 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-tinte-2 uppercase w-16">
                    Zeile
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-tinte-2 uppercase w-20">
                    Status
                  </th>
                  {nameSplitColumn && (
                    <>
                      <th className="px-4 py-2 text-left text-xs font-medium text-akzent uppercase">
                        Vorname (Split)
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-akzent uppercase">
                        Nachname (Split)
                      </th>
                    </>
                  )}
                  {fields
                    .filter((f) => mapping[f.key])
                    .map((field) => (
                      <th
                        key={field.key}
                        className="px-4 py-2 text-left text-xs font-medium text-tinte-2 uppercase"
                      >
                        {field.label}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {currentPageRows.map((row) => (
                  <tr
                    key={row.rowNumber}
                    className={`hover:bg-flaeche-2 ${row.status === 'error' ? 'bg-flaeche-2' : ''}`}
                  >
                    <td className="px-4 py-2 font-mono text-sm">{row.rowNumber}</td>
                    <td className="px-4 py-2">
                      {row.status === 'valid' && (
                        <span className="px-2 py-1 text-xs rounded bg-flaeche-2 text-lauf border border-linie-2">
                          OK
                        </span>
                      )}
                      {row.status === 'error' && (
                        <div>
                          <span className="px-2 py-1 text-xs rounded bg-flaeche-2 text-stoer border border-linie-2">
                            Fehler
                          </span>
                          {row.errors.length > 0 && (
                            <p className="text-xs text-stoer mt-1">{row.errors[0]}</p>
                          )}
                        </div>
                      )}
                      {row.status === 'warning' && (
                        <span className="px-2 py-1 text-xs rounded bg-flaeche-2 text-stoer border border-linie">
                          Warnung
                        </span>
                      )}
                    </td>
                    {nameSplitColumn && (
                      <>
                        <td className="px-4 py-2 text-sm text-akzent">
                          {row.data._parsedFirstName ? (
                            String(row.data._parsedFirstName)
                          ) : (
                            <span className="text-stoer">–</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-akzent">
                          {row.data._parsedLastName ? (
                            String(row.data._parsedLastName)
                          ) : (
                            <span className="text-stoer">–</span>
                          )}
                        </td>
                      </>
                    )}
                    {fields
                      .filter((f) => mapping[f.key])
                      .map((field) => {
                        const csvCol = mapping[field.key];
                        const value = csvCol ? row.data[csvCol] : null;
                        return (
                          <td key={field.key} className="px-4 py-2 text-sm">
                            {value !== null && value !== undefined ? String(value) : '–'}
                          </td>
                        );
                      })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between">
          <button
            onClick={() => setStep('mapping')}
            className="px-4 py-2 border border-linie-2 rounded-lg hover:bg-flaeche-2"
          >
            Zurück
          </button>
          <button
            onClick={handleDryRun}
            disabled={isProcessing}
            className="px-4 py-2 bg-lauf text-akzent-satz rounded-lg hover:bg-lauf disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? 'Importiert...' : `${csvData.length} Datensätze importieren`}
          </button>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────
  // Render: Review Step (Update-Diff Preview)
  // ─────────────────────────────────────────────────────────────

  const renderReviewStep = () => {
    if (!dryRunResult) return null;

    const updateChanges =
      dryRunResult.changes?.filter((c) => c.action === 'update' && c.diffs && c.diffs.length > 0) ||
      [];

    const totalReviewPages = Math.ceil(updateChanges.length / PAGE_SIZE);
    const pagedChanges = updateChanges.slice(reviewPage * PAGE_SIZE, (reviewPage + 1) * PAGE_SIZE);

    const fieldLabels: Record<string, string> = {
      displayName: 'Anzeigename',
      firstName: 'Vorname',
      lastName: 'Nachname',
      email: 'E-Mail',
    };

    return (
      <div className="space-y-4">
        {/* Summary */}
        <div className="p-4 bg-flaeche-2 border border-linie-2 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-stand mt-0.5" />
            <div>
              <h3 className="font-medium text-stand">
                {updateChanges.length} bestehende Datensätze werden geändert
              </h3>
              <p className="text-sm text-stand mt-1">
                Bitte prüfen Sie die Änderungen vor dem Import. Zusätzlich werden{' '}
                {dryRunResult.created} neue Datensätze angelegt.
              </p>
            </div>
          </div>
        </div>

        {/* Toggle für Updates */}
        <div className="flex items-center justify-between p-3 bg-flaeche-2 rounded-lg">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={applyUpdates}
              onChange={(e) => setApplyUpdates(e.target.checked)}
              className="w-4 h-4 rounded border-linie-2 text-akzent focus:ring-akzent"
            />
            <span className="font-medium">Änderungen an bestehenden Datensätzen übernehmen</span>
          </label>
          {!applyUpdates && (
            <span className="text-sm text-stand">Nur neue Datensätze werden angelegt</span>
          )}
        </div>

        {/* Changes Table */}
        <div className="border rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-flaeche-2 border-b flex items-center justify-between">
            <h4 className="font-medium">
              Änderungen {reviewPage * PAGE_SIZE + 1}–
              {Math.min((reviewPage + 1) * PAGE_SIZE, updateChanges.length)} von{' '}
              {updateChanges.length}
            </h4>
            {totalReviewPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setReviewPage((p) => Math.max(0, p - 1))}
                  disabled={reviewPage === 0}
                  className="p-1.5 border border-linie-2 rounded hover:bg-flaeche-2 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-tinte-2">
                  {reviewPage + 1} / {totalReviewPages}
                </span>
                <button
                  onClick={() => setReviewPage((p) => Math.min(totalReviewPages - 1, p + 1))}
                  disabled={reviewPage >= totalReviewPages - 1}
                  className="p-1.5 border border-linie-2 rounded hover:bg-flaeche-2 disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          <div className="divide-y">
            {pagedChanges.map((change, idx) => (
              <div key={idx} className="p-4">
                <div className="font-medium text-tinte mb-2">
                  Personalnummer: {change.externalEmployeeId}
                </div>
                <div className="space-y-1">
                  {change.diffs?.map((diff, dIdx) => (
                    <div key={dIdx} className="flex items-center gap-2 text-sm">
                      <span className="text-tinte-2 w-24">
                        {fieldLabels[diff.field] || diff.field}:
                      </span>
                      <span className="text-stoer line-through">{diff.from || '(leer)'}</span>
                      <ArrowRight className="w-3 h-3 text-tinte-3" />
                      <span className="text-lauf font-medium">{diff.to || '(leer)'}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between">
          <button
            onClick={() => setStep('preview')}
            className="px-4 py-2 border border-linie-2 rounded-lg hover:bg-flaeche-2"
          >
            Zurück
          </button>
          <button
            onClick={handleFinalImport}
            disabled={isProcessing}
            className="px-4 py-2 bg-lauf text-akzent-satz rounded-lg hover:bg-lauf disabled:opacity-50"
          >
            {isProcessing
              ? 'Importiert...'
              : applyUpdates
                ? `${dryRunResult.created + updateChanges.length} Datensätze importieren`
                : `${dryRunResult.created} neue Datensätze anlegen`}
          </button>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────
  // Render: Result Step
  // ─────────────────────────────────────────────────────────────

  const renderResultStep = () => {
    if (!importResult) return null;

    const hasErrors = importResult.errors.length > 0 || (importResult.failed ?? 0) > 0;
    const failedCount = importResult.failed ?? importResult.errors.length;
    const noopCount = importResult.noop ?? 0;

    return (
      <div className="space-y-4">
        {/* Success/Warning Header */}
        <div
          className={`border rounded-lg p-6 ${hasErrors ? 'border-linie-2 bg-flaeche-2' : 'border-linie-2 bg-flaeche-2'}`}
        >
          <div className="flex items-center gap-3 mb-4">
            {hasErrors ? (
              <AlertCircle className="w-8 h-8 text-stand" />
            ) : (
              <CheckCircle className="w-8 h-8 text-lauf" />
            )}
            <div>
              <h3 className={`font-bold text-lg ${hasErrors ? 'text-stand' : 'text-lauf'}`}>
                Import abgeschlossen {hasErrors && '(mit Fehlern)'}
              </h3>
              <p className={hasErrors ? 'text-stand' : 'text-lauf'}>
                {importResult.total} Datensätze verarbeitet
              </p>
            </div>
          </div>

          {/* Counters Grid */}
          <div className="grid grid-cols-5 gap-3 text-center">
            <div className="p-3 bg-flaeche rounded-lg">
              <p className="text-2xl font-bold text-lauf">{importResult.created}</p>
              <p className="text-xs text-tinte-2">Neu</p>
            </div>
            <div className="p-3 bg-flaeche rounded-lg">
              <p className="text-2xl font-bold text-akzent">{importResult.updated}</p>
              <p className="text-xs text-tinte-2">Aktualisiert</p>
            </div>
            <div className="p-3 bg-flaeche rounded-lg">
              <p className="text-2xl font-bold text-tinte-2">{noopCount}</p>
              <p className="text-xs text-tinte-2">Unverändert</p>
            </div>
            <div className="p-3 bg-flaeche rounded-lg">
              <p className="text-2xl font-bold text-tinte-3">{importResult.skipped}</p>
              <p className="text-xs text-tinte-2">Übersprungen</p>
            </div>
            <div
              className={`p-3 bg-flaeche rounded-lg ${failedCount > 0 ? 'ring-2 ring-stoer' : ''}`}
            >
              <p
                className={`text-2xl font-bold ${failedCount > 0 ? 'text-stoer' : 'text-tinte-3'}`}
              >
                {failedCount}
              </p>
              <p className="text-xs text-tinte-2">Fehlerhaft</p>
            </div>
          </div>
        </div>

        {/* Errors List with Navigation */}
        {importResult.errors.length > 0 && (
          <div className="border border-linie-2 rounded-lg">
            <div className="px-4 py-3 bg-flaeche-2 border-b border-linie-2 flex items-center justify-between">
              <h4 className="font-medium text-stoer">{importResult.errors.length} Fehler</h4>
              <div className="flex items-center gap-2 text-sm">
                <button
                  onClick={() => {
                    const container = document.getElementById('error-list');
                    if (container) {
                      const items = container.querySelectorAll('li');
                      const current = Array.from(items).findIndex((el) =>
                        el.classList.contains('ring-2'),
                      );
                      const prevIdx = current <= 0 ? items.length - 1 : current - 1;
                      items.forEach((el, i) => el.classList.toggle('ring-2', i === prevIdx));
                      items[prevIdx]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                  }}
                  className="px-2 py-1 border border-stoer rounded hover:bg-flaeche-2 text-stoer"
                >
                  Vorheriger
                </button>
                <button
                  onClick={() => {
                    const container = document.getElementById('error-list');
                    if (container) {
                      const items = container.querySelectorAll('li');
                      const current = Array.from(items).findIndex((el) =>
                        el.classList.contains('ring-2'),
                      );
                      const nextIdx = current < 0 || current >= items.length - 1 ? 0 : current + 1;
                      items.forEach((el, i) => el.classList.toggle('ring-2', i === nextIdx));
                      items[nextIdx]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                  }}
                  className="px-2 py-1 border border-stoer rounded hover:bg-flaeche-2 text-stoer"
                >
                  Nächster
                </button>
              </div>
            </div>
            <div className="p-4">
              <ul id="error-list" className="text-sm text-stoer space-y-2 max-h-48 overflow-y-auto">
                {importResult.errors.map((err, i) => (
                  <li
                    key={i}
                    className="p-2 bg-flaeche-2 rounded border border-linie-2 hover:bg-flaeche-2 transition-colors"
                  >
                    {err}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Primary: Schließen (wenn onClose vorhanden) */}
        {onClose ? (
          <div className="space-y-2">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-akzent text-akzent-satz rounded-lg hover:bg-akzent"
            >
              Fertig
            </button>
            <button
              onClick={reset}
              className="w-full px-3 py-1.5 text-sm text-tinte-2 hover:text-tinte hover:underline"
            >
              Neuen Import starten
            </button>
          </div>
        ) : (
          <button
            onClick={reset}
            className="w-full px-4 py-2 bg-akzent text-akzent-satz rounded-lg hover:bg-akzent"
          >
            Weiteren Import starten
          </button>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────
  // Main Render
  // ─────────────────────────────────────────────────────────────

  return (
    <div className={className}>
      {title && (
        <div className="mb-4">
          <h3 className="font-semibold text-lg">{title}</h3>
          {description && <p className="text-sm text-tinte-2">{description}</p>}
        </div>
      )}

      {/* Stepper */}
      {renderStepper()}

      {error && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-flaeche-2 text-stoer rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {step === 'upload' && renderUploadStep()}
      {step === 'mapping' && renderMappingStep()}
      {step === 'preview' && renderPreviewStep()}
      {step === 'review' && renderReviewStep()}
      {step === 'result' && renderResultStep()}
    </div>
  );
}
